import { useHierarchy, HierarchyContext } from '@plarium/hierarchy-react';
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';

import { useFormik, FormikContext } from 'formik';
import isEqual from 'lodash/isEqual';
import smoothScrollIntoView from 'smooth-scroll-into-view-if-needed';

import { withNodeManager, useOrderedNodes } from '@gogame/components';

import { FormIsolatedContext } from './FormFieldGroup';

const Form = withNodeManager()(
  React.forwardRef(
    (
      {
        children,
        onSubmit,
        onReset,
        validate,
        validateOnChangeData = true,
        validationSchema,
        initialValues,
        active,
        initialNormalized = true,
        enableReinitializeEqual = false,
        normalizeEffect,
        ...props
      },
      _ref
    ) => {
      const [validateOnChange, setValidateOnChange] = useState(false);
      const [_active, _setActive] = useState(active);
      const [shouldCheckForScroll, setShouldCheckForScroll] = useState(false);
      const submittingMetaRef = useRef(null);
      const ordered = useOrderedNodes();
      const normalizedValuesRef = useRef(null);

      useMemo(() => {
        normalizedValuesRef.current = initialValues;
      }, [initialValues]);

      const validationSchemaWithMeta = useMemo(() => {
        if (typeof validationSchema === 'function') {
          return (field) => {
            return validationSchema(field, submittingMetaRef.current);
          };
        }
        return validationSchema;
      }, [validationSchema]);

      const handleSubmit = async (values, imperativeMethods) => {
        setShouldCheckForScroll(false);
        setValidateOnChange(false);
        try {
          await onSubmit(
            values,
            {
              ...imperativeMethods,
              resetForm, // custom resetForm
              validateForm, // custom validateForm,
              hierarchyState,
              hierarchyUpdate,
            },
            submittingMetaRef.current
          );
        } catch (e) {
          //ignore catch for stepping unhandled error
        }
      };

      const handleReset = (values, imperativeMethods) => {
        setShouldCheckForScroll(false);
        setValidateOnChange(false);
        if (onReset) {
          return onReset(values, imperativeMethods);
        }
      };

      const formik = useFormik({
        ...props,
        onSubmit: handleSubmit,
        onReset: handleReset,
        validationSchema: validationSchemaWithMeta,
        validateOnChange: validateOnChange,
        validateOnBlur: false,
        validateOnMount: false,
        initialValues,
        validate,
      });

      const formikValuesRef = React.useRef(null);

      formikValuesRef.current = formik.values;

      useEffect(() => {
        if (enableReinitializeEqual) {
          formik.resetForm(initialValues);
        }
      }, [enableReinitializeEqual, initialValues]);

      // Formik changes initialValues after reinitializing (initialValues !== formik.initialValues)
      const normalized = useMemo(
        () =>
          initialNormalized &&
          isEqual(formik.values, normalizedValuesRef.current),
        [formik.values]
      );

      const setValues = useCallback(
        (values, options = {}) => {
          if (options.normalized) {
            normalizedValuesRef.current = values;
          }
          formik.setValues(values);
        },
        [formik.setValues]
      );

      const resetForm = useCallback(
        (nextState) => {
          formik.resetForm({ values: nextState });
        },
        [formik.resetForm]
      );

      const submitForm = useCallback(
        (meta) => {
          submittingMetaRef.current = meta;
          setShouldCheckForScroll(true);
          setValidateOnChange(true);
          formik.submitForm();
        },
        [formik.submitForm, setShouldCheckForScroll, setValidateOnChange]
      );

      const validateForm = useCallback(
        (values) => {
          setShouldCheckForScroll(true);
          setValidateOnChange(true);
          return formik.validateForm(values);
        },
        [formik.validateForm, setShouldCheckForScroll, setValidateOnChange]
      );

      const handleNormalize = useCallback(
        ({ normalizedValues, currentValues, changes }, finished) => {
          const newValues = normalizeEffect
            ? normalizeEffect({ normalizedValues, currentValues, changes })
            : normalizedValues;
          setValues(newValues, { normalized: finished });
        },
        [setValues, normalizeEffect]
      );

      const handleDataLoad = useCallback(() => {
        if (validateOnChange && validateOnChangeData) {
          formik.validateForm(formik.values);
        }
      }, [
        validateOnChangeData,
        validateOnChange,
        formik.validateForm,
        formik.values,
      ]);

      const {
        registerArea,
        unregisterArea,
        state: hierarchyState,
        update: hierarchyUpdate,
        loadingCount,
        active: hierarchyActive,
      } = useHierarchy({
        values: formik.values,
        active: _active,
        onNormalize: handleNormalize,
        onDataLoad: handleDataLoad,
        normalized,
      });

      const formikBag = {
        ...formik,
        setValues,
        submitForm,
        validateForm,
        resetForm,
        submittingMetaRef,
        validateOnChange,
        hierarchyUpdate,
        hierarchyState,
        loadingCount,
        active: hierarchyActive,
      };

      useEffect(() => {
        _setActive(active);
      }, [active]);

      useEffect(() => {
        if (shouldCheckForScroll && ordered.length > 0) {
          smoothScrollIntoView(ordered[0], {
            scrollMode: 'if-needed',
            block: 'center',
            inline: 'start',
          }).then(() => setShouldCheckForScroll(false));
        }
      }, [shouldCheckForScroll, ordered]);

      return (
        <FormIsolatedContext>
          <FormikContext.Provider value={formikBag}>
            <HierarchyContext.Provider
              value={{
                registerArea,
                unregisterArea,
                loadingCount,
                active: hierarchyActive,
                state: hierarchyState,
                update: hierarchyUpdate,
              }}
            >
              {typeof children === 'function'
                ? children({ ...formikBag, hierarchyState })
                : children}
            </HierarchyContext.Provider>
          </FormikContext.Provider>
        </FormIsolatedContext>
      );
    }
  )
);

export default Form;
